import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { CircleDollarSign, Copy, Moon, Star } from "lucide-react";
import { AuthUser, LanguageNameSpaces, WorkspaceType } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { useUserInfo } from "@shared/utils/hooks/misc.ts";
import UserAvatar from "common/Avatars/UserAvatar.tsx";
import WorkspaceSwitch from "common/Home/Header/WorkspaceSwitch.tsx";
import UpgradeButton from "common/Home/Header/UpgradeButton.tsx";
import DarkModeToggle from "common/Home/Header/DarkModeToggle.tsx";
import LanguageSelector from "common/Home/Header/LanguageSelector.tsx";
import NewLookToggle from "features/newLook/NewLookToggle.tsx";
import useCanToggleNewLook from "features/newLook/useCanToggleNewLook.ts";
import GroupSelect from "features/admin/group/GroupSelect.tsx";
import CurrencyToggle from "common/Home/Sider/CurrencyToggle.tsx";
import WorkspaceSelector from "common/Home/Sider/WorkspaceSelector.tsx";
import QrPopover from "common/Home/Sider/QrPopover.tsx";
import { SocialIconLinks } from "common/Home/Sider/SocialMedia.tsx";
import { BASE_BILLING_SETTINGS_PATH } from "common/pathConstants.ts";
import { IS_WEB_VIEW, IS_WHOP } from "common/constants.ts";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import {
  copyCodeToClipboard,
  openMessage,
  showIntercomSpace,
} from "utils/misc.tsx";

const ICON_PROPS = { size: 20, strokeWidth: 1.75 };

interface NewLookMobileMenuProps {
  user?: AuthUser;
  workspace?: WorkspaceType;
  /** The antd menu with the page links (Trading Terminal, MCP, Settings...). */
  menu: React.ReactNode;
  adminBanner?: React.ReactNode;
  showUpgradeButton: boolean;
  showWorkspaceSelector: boolean;
  showWorkspaceSwitch: boolean;
  showGroupSelect: boolean;
  canManagePlan: boolean;
  showSocial: boolean;
  isWhitelabel: boolean;
  onLogout?: () => void;
}

/** The phone "Menu" drawer's content in the new look. */
const NewLookMobileMenu = ({
  user,
  workspace,
  menu,
  adminBanner,
  showUpgradeButton,
  showWorkspaceSelector,
  showWorkspaceSwitch,
  showGroupSelect,
  canManagePlan,
  showSocial,
  isWhitelabel,
  onLogout,
}: NewLookMobileMenuProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const canToggleNewLook = useCanToggleNewLook();
  const { userInfoItems, uidItem } = useUserInfo();
  const planItem = userInfoItems[0];
  const expiryItem = userInfoItems[1];

  return (
    <Scroll>
      {user && (
        <Profile>
          <UserAvatar user={user} avatarSize={48} fallbackInitialsSize={4} />
          <Who>
            <Name>{user.full_name || user.email}</Name>
            {user.full_name && <Email>{user.email}</Email>}
            {uidItem && (
              <Uid>
                {uidItem.key} {uidItem.value}
                <CopyButton
                  type="button"
                  aria-label={t("sider.copied")}
                  onClick={async () => {
                    await copyCodeToClipboard(String(uidItem.value));
                    openMessage(t("sider.copied"), "success", 2);
                  }}
                >
                  <Copy size={14} strokeWidth={1.75} />
                </CopyButton>
              </Uid>
            )}
          </Who>
        </Profile>
      )}
      {planItem && (
        <PlanCard>
          <PlanText>
            <PlanName>{planItem.value}</PlanName>
            {expiryItem && (
              <PlanMeta $danger={expiryItem.type === "danger"}>
                {expiryItem.key} {expiryItem.value}
              </PlanMeta>
            )}
          </PlanText>
          {canManagePlan && (
            <ManageLink to={BASE_BILLING_SETTINGS_PATH}>
              {t("sider.manage")}
            </ManageLink>
          )}
        </PlanCard>
      )}
      {showUpgradeButton && (
        <UpgradeWrapper>
          <UpgradeButton style={{ alignSelf: "center" }} />
        </UpgradeWrapper>
      )}
      {showWorkspaceSwitch && (
        <SwitchWrapper>
          <WorkspaceSwitch />
        </SwitchWrapper>
      )}
      {showWorkspaceSelector && (
        <Section>
          <SectionLabel>{t("workspace.workspaces")}</SectionLabel>
          <Rows>
            <WorkspaceSelector workspace={workspace} row />
          </Rows>
          {showGroupSelect && (
            <GroupSelect style={{ marginTop: 8, width: "100%" }} />
          )}
        </Section>
      )}
      {!user?.is_onboarding && (
        <Section>
          <SectionLabel>{t("sider.preferences")}</SectionLabel>
          <Rows>
            <Row>
              <CurrencyToggle
                icon={<CircleDollarSign {...ICON_PROPS} />}
                style={{ width: "100%" }}
              />
            </Row>
            {!IS_WHOP && (
              <Row>
                <DarkModeToggle icon={<Moon {...ICON_PROPS} />} />
              </Row>
            )}
            {canToggleNewLook && (
              <Row>
                <NewLookToggle icon={<Star {...ICON_PROPS} />} />
              </Row>
            )}
            <Row>
              <LanguageSelector row />
            </Row>
          </Rows>
        </Section>
      )}
      <Section>
        <SectionLabel>{t("sider.more")}</SectionLabel>
        <MoreMenu>{menu}</MoreMenu>
      </Section>
      {adminBanner}
      <Footer>
        <TextLinks>
          {!isWhitelabel && (
            <>
              <TextLink type="button" onClick={() => showIntercomSpace("help")}>
                {t("sider.how_to_use")}
              </TextLink>
              <TextLink type="button" onClick={() => showIntercomSpace("home")}>
                {t("sider.support")}
              </TextLink>
            </>
          )}
          {onLogout && (
            <TextLink type="button" onClick={onLogout}>
              {t("sider.logout")}
            </TextLink>
          )}
        </TextLinks>
        {showSocial && (
          <SocialRow>
            <SocialIconLinks />
          </SocialRow>
        )}
        {showSocial && !IS_WEB_VIEW && !IS_WHOP && (
          <Stores>
            <QrPopover isAppStore={true} />
            <QrPopover isAppStore={false} />
          </Stores>
        )}
      </Footer>
    </Scroll>
  );
};

export default NewLookMobileMenu;

const GUTTER = 18;

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-top: 8px;
`;
const Profile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px ${GUTTER}px 16px;
`;
const Who = styled.div`
  min-width: 0;
`;
const Name = styled.div`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Email = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const PlanCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 ${GUTTER}px;
  padding: 10px 14px;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  background: ${(p) => p.theme.colors.lightBlue};
`;
const PlanText = styled.div`
  min-width: 0;
`;
const PlanName = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: 560;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const PlanMeta = styled.div<{ $danger?: boolean }>`
  font-size: 13px;
  line-height: 18px;
  color: ${(p) =>
    p.$danger
      ? getNewLookTokens(p.theme).down
      : getNewLookTokens(p.theme).secondary};
`;
const ManageLink = styled(Link)`
  flex: none;
  font-size: 14px;
  font-weight: 560;
  color: ${(p) => p.theme.colors.primaryBlue};
`;
const UpgradeWrapper = styled.div`
  display: flex;
  margin: 12px ${GUTTER}px 0;
`;
const SwitchWrapper = styled.div`
  margin: 12px ${GUTTER}px 0;
`;
const Section = styled.div`
  margin: 18px ${GUTTER}px 0;
`;
const SectionLabel = styled.div`
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  padding: 0 0 4px 2px;
`;
const Rows = styled.div`
  display: flex;
  flex-direction: column;
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  min-height: 44px;
  > * {
    width: 100%;
  }
`;
// The antd menu, reduced to 48px hairline-separated rows with a chevron.
const MoreMenu = styled.div`
  .ant-menu {
    background: transparent;
    border: none;
    margin-top: 0 !important;
  }
  .ant-menu-item-group-title {
    display: none;
  }
  .ant-menu-item-group-list {
    padding: 0;
  }
  .ant-menu-item,
  .ant-menu-submenu-title {
    display: flex;
    align-items: center;
    height: 44px !important;
    line-height: 44px;
    margin: 0 -8px !important;
    width: calc(100% + 16px) !important;
    padding-inline: 8px !important;
    border-radius: ${NEW_LOOK_RADIUS.control}px !important;
    gap: 12px;
    color: ${(p) => getNewLookTokens(p.theme).text};
    background: transparent !important;
  }
  .ant-menu-item:hover,
  .ant-menu-submenu-title:hover,
  .ant-menu-item:active,
  .ant-menu-submenu-title:active {
    background: ${(p) => getNewLookTokens(p.theme).rowHover} !important;
  }
  .ant-menu-item .ant-menu-item-icon,
  .ant-menu-submenu-title .ant-menu-item-icon {
    margin: 0 !important;
    min-width: 20px;
    font-size: 20px;
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
  .ant-menu-item .ant-menu-item-icon,
  .ant-menu-submenu-title .ant-menu-item-icon,
  .ant-menu-item .ant-menu-item-icon svg,
  .ant-menu-submenu-title .ant-menu-item-icon svg {
    width: 20px;
    height: 20px;
    flex: none;
  }
  .ant-menu-item-icon + .ant-menu-title-content,
  .ant-menu-item-icon + span {
    margin-inline-start: 0 !important;
  }
  .ant-menu-title-content {
    flex: 1;
    margin: 0 !important;
    font-size: 14px;
    [class*="MenuItemTextStyled"] {
      padding-left: 0 !important;
    }
    a {
      color: inherit;
      padding-left: 0 !important;
      height: 44px;
    }
    [class*="StyledTag"] {
      margin-left: 8px !important;
    }
  }
  .ant-menu-item-selected,
  .ant-menu-item-selected .ant-menu-title-content,
  .ant-menu-submenu-selected > .ant-menu-submenu-title {
    color: ${(p) => p.theme.colors.primaryBlue};
  }
  .ant-menu-item-selected .ant-menu-item-icon {
    color: ${(p) => p.theme.colors.primaryBlue};
  }
  /* A chevron at the end of every link row. */
  .ant-menu-item::after {
    content: "";
    position: static !important;
    flex: none;
    width: 16px;
    height: 16px;
    border: none;
    opacity: 1;
    transform: none;
    background-color: ${(p) => getNewLookTokens(p.theme).muted};
    -webkit-mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'><path d='m9 18 6-6-6-6'/></svg>")
      center / contain no-repeat;
    mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'><path d='m9 18 6-6-6-6'/></svg>")
      center / contain no-repeat;
  }
  .ant-menu-submenu-arrow {
    color: ${(p) => getNewLookTokens(p.theme).muted};
  }
  .ant-menu-sub.ant-menu-inline {
    background: transparent;
  }
  .ant-menu-sub .ant-menu-item {
    padding-inline-start: 32px !important;
    height: 44px !important;
  }
`;
const Footer = styled.div`
  padding: 16px ${GUTTER}px calc(16px + env(safe-area-inset-bottom));
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  margin-top: auto;
  padding-top: 16px;
`;
const TextLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
`;
const TextLink = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  height: 28px;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const SocialRow = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 8px;
  a {
    display: inline-flex;
    height: 24px;
    align-items: center;
  }
  svg {
    transform: none !important;
  }
`;
const Uid = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  font-size: 12.5px;
  line-height: 18px;
  font-variant-numeric: tabular-nums;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  &:hover {
    color: ${(p) => getNewLookTokens(p.theme).ink};
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
`;
const Stores = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 12px;
`;
